import { createFileRoute, Outlet } from "@tanstack/react-router";
import { scheduledChangesQueryOptions } from "@/app/billing/api/get-scheduled-changes";
import { subscriptionQueryOptions } from "@/app/billing/api/get-subscriptions";
import { BillingTabs } from "@/app/billing/billing-tabs";
import { BillingRouteErrorBoundary } from "@/app/billing/components/billing-route-error-boundary";
import { ORG } from "@/app/capabilities/permissions";
import { NotFoundRoute } from "@/components/custom/router/not-found-route";
import {
  requireBillingPermission,
  requireProjectCapability,
} from "@/lib/route-guards";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/billing"
)({
  beforeLoad: async (ctx) => {
    // requireProjectCapability throws notFound() (404) when the billing capability
    // is off: billing does not exist for this org. That is distinct from a denied
    // billing permission, which is a 403 (forbidden) gated by permission guards.
    await requireProjectCapability(ctx, "billing");
    // Billing exists for the org; now require permission to view it (allow-all
    // while RBAC is off, 403 for a covered caller without `billing_read`).
    await requireBillingPermission(ctx, ORG.billing.read);
  },
  loader: ({ context: { queryClient }, params: { organizationId } }) => {
    queryClient.prefetchQuery(subscriptionQueryOptions({ organizationId }));
    queryClient.prefetchQuery(scheduledChangesQueryOptions({ organizationId }));
    return { crumb: "Billing" };
  },
  component: BillingLayout,
  // Catch billing errors here so child routes without their own boundary
  // (plan selection, checkout, processing) surface a billing-provider outage
  // message instead of bubbling to an ancestor that blames the org/project URL.
  errorComponent: BillingRouteErrorBoundary,
  // Render the generic 404 here rather than letting the not-found bubble to an
  // ancestor's IncompleteUrlFallback, which wrongly blames the org/project URL.
  notFoundComponent: NotFoundRoute,
});

function BillingLayout() {
  return (
    <div className="flex flex-col gap-6">
      <BillingTabs />
      <div className="min-h-[calc(100vh-16rem)]">
        <Outlet />
      </div>
    </div>
  );
}
